import {animate, state, style, transition, trigger} from '@angular/animations';
import {Component, HostListener, OnInit} from '@angular/core';
import {NavigationExtras, Router} from '@angular/router';
import {Subject} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {MenuItem, MessageService} from 'primeng/api';
import {LANGUAGES, LangService} from '../services/lang.service';
import {BuilderMenu} from './menu';

import {Menu} from './menu-bar/types';
import {TranslateService} from '@ngx-translate/core';
import {AccountService} from '../auth/account.service';
import {ActivityTypeEnum, GatewayPublicUserService, GatewayUserActivityService, UserDTO} from '@tj/tj-dmp-lib-api';
import {KeycloakService} from 'keycloak-angular';
import {environment} from "../../../environments/environment";

@Component({
    selector: 'app-layout',
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    animations: [
        trigger('openCloseSidebar', [
            state('open', style({
                width: '240px',
            })),
            state('closed', style({
                width: '60px',
            })),
            transition('open => closed', [animate('0.3s')]),
            transition('closed => open', [animate('0.3s')]),
        ]),
        trigger('openCloseMain', [
            state('open', style({
                marginLeft: '240px',
            })),
            state('closed', style({
                marginLeft: '60px',
            })),
            state('mobile', style({
                marginLeft: 0,
            })),
            transition('open => closed', [animate('0.3s')]),
            transition('closed => open', [animate('0.3s')]),
        ]),
    ],
    standalone: false
})
export class LayoutComponent implements OnInit {

    constructor(private readonly router: Router,
                private readonly langService: LangService,
                private readonly accountService: AccountService,
                private readonly translate: TranslateService,
                private readonly keycloakService: KeycloakService,
                private readonly gatewayUserActivityService: GatewayUserActivityService,
                private readonly gatewayPublicUserService: GatewayPublicUserService,
                private readonly messageService: MessageService) {
        this.onResize();
    }

    get connection() {
        // return !environment.allowWithoutLogin;
        return true;
    }

    get currentYear() {
        return new Date().getFullYear();
    }

    show_sidebar = false;
    userItems: MenuItem[] = [];

    userMenuOpen = false;

    current_lang = 'fr';

    pages: Menu[] = [];

    logo = ['assets/img/logo-app-white.svg', 'assets/img/icon-app-white.svg'];

    isMinimized = false;

    eventsSubject: Subject<void> = new Subject<void>();

    screenWidth: number;

    user: UserDTO;

    get displayName(): string {
        const fullName = [this.user.firstName, this.user.lastName].filter(Boolean).join(' ');
        return fullName || this.user.login;
    }

    @HostListener('window:resize', ['$event'])
    onResize(_event?: Event) {
        this.screenWidth = window.innerWidth;
    }

    ngOnInit() {
        this.accountService.identity().then((e) => {
            if(!e) {
                const navigationExtras: NavigationExtras = {state: {message: 'login'}};
                // this.router.navigate([''], navigationExtras);
            }
        }, () => {
            const navigationExtras: NavigationExtras = {state: {message: 'login'}};
            // this.router.navigate([''], navigationExtras);
        })
        this.router.events.subscribe(() => this.closeSidebar());
        this.langService.currentLang$.subscribe((lang) => {
            this.current_lang = lang;
            this.pages = BuilderMenu.get(this.translate);
            this.userItems = this.buildUserItems();
        });
        this.accountService.getAuthenticationState().subscribe(user => {
            this.user = user;
        });
    }

    openSidebar() {
        this.show_sidebar = true;
    }

    closeSidebar() {
        this.show_sidebar = false;
    }

    closeMinimizeSubnav(minimize: boolean) {
        if (!minimize || this.isMinimized) {
            this.eventsSubject.next();
        }
    }

    logout(): void {
        this.keycloakService.init({
            config: {
                url: environment.KeycloakUrl,
                realm: environment.KeycloakRealm,
                clientId: environment.KeycloakClientId
            },
            // https://stackoverflow.com/questions/72019588/how-to-avoid-timeout-when-waiting-for-3rd-party-check-iframe-message-with-keyc
            initOptions: {
                pkceMethod: 'S256',
                checkLoginIframe: false,
                redirectUri: environment.URL_DMP,
            }
        }).then((authenticated) => {
            // Try to log activity but don't block logout if it fails (e.g., token already invalidated)
            this.gatewayUserActivityService.logCustomActivity(ActivityTypeEnum.LOGOUT).subscribe({
                next: () => {
                    // Clear storage before logout
                    localStorage.clear();
                    sessionStorage.clear();
                    this.keycloakService.logout().then(res => {
                        console.log(res);
                    });
                },
                error: (err) => {
                    // If logging fails (e.g., 401), still proceed with logout
                    console.warn('Failed to log logout activity:', err);
                    // Clear storage before logout
                    localStorage.clear();
                    sessionStorage.clear();
                    this.keycloakService.logout().then(res => {
                        console.log(res);
                    });
                }
            });
        });
        // this.loginService.logout();
        // const navigationExtras: NavigationExtras = {state: {message: 'logout'}};
        // this.router.navigate(['/'], navigationExtras);
    }

    minimize() {
        this.isMinimized = !this.isMinimized;
        this.closeMinimizeSubnav(false);
    }

    goParameters() {
        this.router.navigate(['/h/account']);
    }

    changeLang(lang: string) {
        if (lang === this.current_lang) {
            return;
        }
        // Saved on the user, as the account page does: the user's langKey is what gets applied at startup.
        this.gatewayPublicUserService.getCurrentUser().pipe(
            switchMap(user => this.gatewayPublicUserService.updateCurrentUser({...user, langKey: lang}))
        ).subscribe({
            // Reload, so that labels computed once with translate.instant() are rebuilt in the new language.
            next: () => this.langService.changeLang(lang, false),
            error: () => this.messageService.add({
                key: 'common', severity: 'error', summary: this.translate.instant('language.saveError.summary'),
                detail: this.translate.instant('language.saveError.detail')
            }),
        });
    }

    private buildUserItems(): MenuItem[] {
        const currentLang = LANGUAGES.find(lang => lang.code === this.current_lang) ?? LANGUAGES[0];
        return [
            {
                label: this.translate.instant('menu.users.account'),
                icon: 'fas fa-user-cog',
                command: () => this.goParameters(),
            },
            {
                label: `${this.translate.instant('language.label')} : ${currentLang.label}`,
                ...this.flagIcon(currentLang.code),
                items: LANGUAGES.map(lang => ({
                    label: lang.label,
                    ...this.flagIcon(lang.code),
                    styleClass: lang.code === currentLang.code ? 'tj-lang-current' : undefined,
                    command: () => this.changeLang(lang.code),
                })),
            },
            {separator: true},
            {
                label: this.translate.instant('menu.users.logout'),
                icon: 'fas fa-sign-out-alt',
                command: () => this.logout(),
            },
        ];
    }

    // The menu only takes an icon class, so the flag image is passed through the icon's style (see .tj-flag in styles.scss).
    private flagIcon(code: string): Pick<MenuItem, 'icon' | 'iconStyle'> {
        return {icon: 'tj-flag', iconStyle: {'background-image': `url(assets/img/flag/${code}.jpg)`}};
    }
}
